/**
 * W-18 收款账户(SELLER,只读)。视觉真源 design/withdraw-account.html。
 *
 * FR-12=B(epics 008.6):**不做主动提现** —— 后端无提现执行端点,结算为 T+N 系统自动放款。
 * 走查简化:收款账户 = 实名认证四要素银行卡(实名通过后自动建为默认收款账户,见 KycVerifyUseCase),
 *   本页**只读展示**该卡 + 到账记录;不再提供手动绑卡 / 微信 / 切主卡(款项去向由实名卡确定)。
 * KYC 前置:所有 `/wallet/withdraw-accounts/*` 带 @IdentityStateRequired(KYC_VERIFIED);未实名 010303。
 */
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listWithdrawAccounts } from '@/api/wallet';
import { useAuthStore } from '@/store/auth';
import { PayoutHistory } from '@/components/wallet/PayoutHistory';

const VERIFIED_STATES = new Set(['KYC_VERIFIED', 'WITHDRAW_BOUND']);

export function WithdrawAccountPage() {
  const navigate = useNavigate();
  const identityState = useAuthStore((s) => s.identityState);
  const verified = !!identityState && VERIFIED_STATES.has(identityState);

  const accountsQuery = useQuery({
    queryKey: ['withdraw-accounts'],
    queryFn: listWithdrawAccounts,
  });

  const accounts = accountsQuery.data ?? [];
  const primary = accounts.find((a) => a.primary) ?? accounts[0] ?? null;

  return (
    <div className="inner section">
      <div className="sm muted mb-6">
        <Link to="/">首页</Link> / <Link to="/earnings">我的收益</Link> / 收款账户
      </div>

      <div className="row between mb-6">
        <h1 className="h2">收款账户</h1>
        <Link to="/earnings" className="sm muted">
          ← 回收益
        </Link>
      </div>

      {!verified && (
        <section
          className="card card-pad mb-8"
          style={{ borderLeft: '3px solid var(--color-primary)' }}
        >
          <div className="row between wrap gap-4">
            <div>
              <div style={{ fontWeight: 'var(--weight-medium)' }}>完成实名认证后将自动生成收款账户</div>
              <div className="xs muted mt-1">实名认证的银行卡即为你的收款账户,用于接收结算款项。</div>
            </div>
            <button type="button" className="btn btn-primary" onClick={() => navigate('/kyc-verify')}>
              去实名
            </button>
          </div>
        </section>
      )}

      {/* 到账方式(FR-12=B:自动结算,无手动提现) */}
      <section className="card card-pad mb-8">
        <div className="sm muted">到账方式</div>
        <div className="mt-2" style={{ fontWeight: 'var(--weight-medium)' }}>
          T+7 自动结算 · 无需手动提现
        </div>
        <div className="xs muted mt-3">
          订单完成后,平台按结算规则(抽佣 5%,卖家到账 95%)自动将款项结算至你的收款账户,到账明细见下方「到账记录」。
        </div>
      </section>

      {/* 收款账户(只读:实名卡) */}
      <section className="mb-8">
        <h3 className="h3 mb-4">收款账户</h3>

        {accountsQuery.isLoading ? (
          <div className="state">
            <p className="muted sm">加载账户中…</p>
          </div>
        ) : primary ? (
          <>
            <div className="list">
              <div className="list-item">
                <span className="avatar" style={{ borderRadius: 'var(--radius-md)' }}>
                  银
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 'var(--weight-medium)' }}>银行储蓄卡</div>
                  <div className="xs muted mt-1">{primary.maskedAccountNo ?? '已绑定'}</div>
                </div>
              </div>
            </div>
            <p className="xs muted mt-3">收款账户为你实名认证的银行卡,款项将结算至此。</p>
          </>
        ) : verified ? (
          <div className="state">
            <div className="ic">🏦</div>
            <p className="muted sm">收款账户生成中,请稍后刷新查看。</p>
          </div>
        ) : (
          <div className="state">
            <div className="ic">🏦</div>
            <p className="muted sm">完成实名认证后自动生成收款账户。</p>
          </div>
        )}
      </section>

      {/* 到账记录(= earnings/history 已打款流水) */}
      <PayoutHistory title="到账记录" emptyText="暂无到账记录" />
    </div>
  );
}
